import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Send, Download, CheckCircle2, RefreshCw, ChevronRight, Trash2, ArrowLeft, Eye, EyeOff, Upload, Mail, Users, Wallet, FileText, X, Save } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Skeleton, Modal } from "@/components/ui-kit";
import { formatIDR } from "@/lib/format";

/**
 * Iter v34b2 — Payroll Otomatis
 * List batches → detail per batch → edit per item → approve → generate PDF → send email
 */
export default function Payroll() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Iter v34b6 — Create batch config
  const [form, setForm] = useState({
    period_type: "monthly",
    period: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; })(),
    start_date: "",
    end_date: "",
    component_ids: null,  // null = default (semua enabled_by_default)
    department: "",       // Iter v34b8 — "" = keseluruhan
  });
  const [components, setComponents] = useState([]);
  const [departments, setDepartments] = useState([]);
  // Iter v34ch — Arsip batch yang di-soft-delete
  const [archived, setArchived] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveDetail, setArchiveDetail] = useState(null);
  const [archiveDetailLoading, setArchiveDetailLoading] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/payroll/batches");
      setBatches(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat"); }
    finally { setLoading(false); }
  };
  const loadArchived = async () => {
    setArchivedLoading(true);
    try {
      const r = await api.get("/payroll/deleted-batches");
      setArchived(r.data || []);
    } catch { /* silent */ }
    finally { setArchivedLoading(false); }
  };
  const loadComponents = () => api.get("/payroll/components").then((r) => setComponents(r.data || [])).catch(() => {});
  const loadDepartments = () => api.get("/masters/department-categories").then((r) => setDepartments(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadComponents(); loadDepartments(); loadArchived(); }, []);

  const openCreate = () => {
    // Iter v34b6 — default: pilih semua komponen enabled_by_default
    Promise.all([loadComponents(), loadDepartments()]).then(() => {
      setForm((f) => ({ ...f, component_ids: null, department: "" }));
      setCreateOpen(true);
    });
  };

  const createBatch = async () => {
    // Validation
    if (form.period_type === "monthly" && !/^\d{4}-\d{2}$/.test(form.period)) {
      toast.error("Format periode: YYYY-MM"); return;
    }
    if (form.period_type === "custom" && (!form.start_date || !form.end_date)) {
      toast.error("Tanggal mulai & selesai wajib diisi"); return;
    }
    setCreating(true);
    try {
      const payload = {
        period_type: form.period_type,
        ...(form.period_type === "monthly" ? { period: form.period } : { start_date: form.start_date, end_date: form.end_date }),
        component_ids: form.component_ids,  // null → default
        department: form.department || null,  // Iter v34b8 — null/empty = keseluruhan
      };
      const r = await api.post("/payroll/batches", payload);
      toast.success(`Batch dibuat: ${r.data.period}`);
      setCreateOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat batch"); }
    finally { setCreating(false); }
  };

  const del = async (id, per) => {
    // Iter v34ch — Soft delete: prompt reason, archive to history
    const reason = window.prompt(`Hapus batch ${per}?\n\nBatch akan dipindah ke arsip Riwayat dan bisa dilihat detailnya. Isi alasan penghapusan:`, "");
    if (reason === null) return; // Cancel
    try {
      await api.delete(`/payroll/batches/${id}`, { params: reason ? { reason } : {} });
      toast.success("Batch dipindah ke arsip");
      load();
      loadArchived();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  const activeComponentIds = form.component_ids === null
    ? components.filter((c) => c.enabled_by_default).map((c) => c.id)
    : form.component_ids;
  const toggleComp = (id) => {
    const curr = activeComponentIds;
    const next = curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id];
    setForm((f) => ({ ...f, component_ids: next }));
  };

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1500px] mx-auto space-y-6">
        <PageHeader
          overline="HR · Payroll"
          title="Payroll Otomatis"
          description="Kelola batch payroll bulanan atau mingguan (rentang tanggal). Auto-hitung gaji pokok, upah lembur, potongan bon, dan komponen master (uang makan, BPJS, dsb)."
          action={
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => navigate("/payroll/components")} data-testid="payroll-master-components-btn">
                <FileText className="h-3.5 w-3.5"/> Master Komponen
              </Button>
              <Button onClick={openCreate} data-testid="payroll-open-create-btn">
                <Plus className="h-3.5 w-3.5"/> Buat Batch Baru
              </Button>
            </div>
          }
        />

        {/* Batch list */}
        <Card className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full"/>)}</div>
          ) : batches.length === 0 ? (
            <div className="text-center py-16 text-stone-400" data-testid="payroll-empty">Belum ada batch payroll. Klik &quot;Buat Batch Baru&quot; di atas.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payroll-batches-table">
                <thead className="bg-stone-50">
                  <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 border-b">
                    <th className="py-3 px-3">Periode</th>
                    <th className="py-3 px-3">Tipe</th>
                    <th className="py-3 px-3">Departemen</th>
                    <th className="py-3 px-3">Status</th>
                    <th className="py-3 px-3 text-right">Karyawan</th>
                    <th className="py-3 px-3 text-right">Total THP</th>
                    <th className="py-3 px-3 text-right">Terkirim</th>
                    <th className="py-3 px-3">Dibuat</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} className="border-b border-stone-100 hover:bg-stone-50/50 cursor-pointer" onClick={() => navigate(`/payroll/${b.id}`)} data-testid={`payroll-batch-${b.id}`}>
                      <td className="py-3 px-3 font-mono-num font-medium text-stone-900">{b.period}</td>
                      <td className="py-3 px-3 text-[11px] text-stone-600">{b.period_type === "custom" ? "Mingguan/Custom" : "Bulanan"}</td>
                      <td className="py-3 px-3 text-[11px] text-stone-700">{b.department || <span className="text-stone-400 italic">Keseluruhan</span>}</td>
                      <td className="py-3 px-3"><StatusPill s={b.status}/></td>
                      <td className="py-3 px-3 text-right font-mono-num">{b.employee_count}</td>
                      <td className="py-3 px-3 text-right font-mono-num font-semibold text-blue-800">{formatIDR(b.total_thp || 0)}</td>
                      <td className="py-3 px-3 text-right font-mono-num text-emerald-700">{b.sent_count}/{b.employee_count}</td>
                      <td className="py-3 px-3 text-[11px] text-stone-500">{new Date(b.created_at).toLocaleDateString("id-ID")}</td>
                      <td className="py-3 px-3 text-right">
                        <button onClick={(e) => { e.stopPropagation(); del(b.id, b.period); }} className="text-stone-400 hover:text-red-700" data-testid={`payroll-del-${b.id}`}><Trash2 className="h-4 w-4"/></button>
                        <ChevronRight className="h-4 w-4 text-stone-400 inline ml-2"/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Iter v34ch — Riwayat / Arsip Batch Dihapus */}
        <Card className="p-0" data-testid="payroll-archived-card">
          <div className="p-4 md:p-5 border-b border-stone-100 flex items-center gap-3">
            <FileText className="h-4 w-4 text-amber-700"/>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-stone-800">Riwayat Batch Dihapus</h3>
              <p className="text-[11px] text-stone-500">Batch yang sudah dihapus tetap tersimpan sebagai arsip beserta seluruh detailnya.</p>
            </div>
            <span className="text-[11px] font-mono bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full" data-testid="payroll-archived-count">{archived.length}</span>
            <button onClick={loadArchived} className="text-stone-400 hover:text-stone-800" title="Refresh" data-testid="payroll-archived-refresh">
              <RefreshCw className="h-4 w-4"/>
            </button>
          </div>
          {archivedLoading ? (
            <div className="p-4 space-y-2">{Array.from({length:2}).map((_,i) => <Skeleton key={i} className="h-12"/>)}</div>
          ) : archived.length === 0 ? (
            <div className="text-center py-8 text-stone-400 text-sm" data-testid="payroll-archived-empty">Belum ada batch yang dihapus.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payroll-archived-table">
                <thead className="bg-amber-50/50 text-left text-[11px] uppercase tracking-widest text-stone-500 border-b border-stone-100">
                  <tr>
                    <th className="py-2.5 px-3">Periode</th>
                    <th className="py-2.5 px-3">Tipe</th>
                    <th className="py-2.5 px-3">Departemen</th>
                    <th className="py-2.5 px-3 text-right">Karyawan</th>
                    <th className="py-2.5 px-3 text-right">Total THP</th>
                    <th className="py-2.5 px-3">Dihapus oleh</th>
                    <th className="py-2.5 px-3">Alasan</th>
                    <th className="py-2.5 px-3">Dihapus pada</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {archived.map((b) => (
                    <tr key={b.id} className="border-b border-stone-100 hover:bg-amber-50/40 cursor-pointer" data-testid={`payroll-archived-${b.id}`} onClick={async () => {
                      setArchiveDetail({ ...b });
                      setArchiveOpen(true);
                      setArchiveDetailLoading(true);
                      try {
                        const r = await api.get(`/payroll/deleted-batches/${b.id}`);
                        setArchiveDetail(r.data);
                      } catch (e) { toast.error(e?.response?.data?.detail || "Gagal muat detail"); }
                      finally { setArchiveDetailLoading(false); }
                    }}>
                      <td className="py-2.5 px-3 font-mono-num font-medium text-stone-900">{b.period}</td>
                      <td className="py-2.5 px-3 text-[11px] text-stone-600">{b.period_type === "custom" ? "Mingguan/Custom" : "Bulanan"}</td>
                      <td className="py-2.5 px-3 text-[11px] text-stone-700">{b.department || <span className="text-stone-400 italic">Keseluruhan</span>}</td>
                      <td className="py-2.5 px-3 text-right font-mono-num">{b.employee_count || 0}</td>
                      <td className="py-2.5 px-3 text-right font-mono-num text-blue-800">{formatIDR(b.total_thp || 0)}</td>
                      <td className="py-2.5 px-3 text-[11px] text-stone-700">{b.deleted_by || "-"}</td>
                      <td className="py-2.5 px-3 text-[11px] text-stone-600 max-w-[180px] truncate" title={b.delete_reason}>{b.delete_reason || <span className="text-stone-400 italic">-</span>}</td>
                      <td className="py-2.5 px-3 text-[11px] text-stone-500">{b.deleted_at ? new Date(b.deleted_at).toLocaleString("id-ID", {day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit"}) : "-"}</td>
                      <td className="py-2.5 px-3 text-right"><ChevronRight className="h-4 w-4 text-stone-400 inline"/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Iter v34ch — Detail Modal untuk Arsip Batch */}
      <Modal open={archiveOpen} onClose={() => setArchiveOpen(false)} size="xl" title={archiveDetail ? `Arsip: Batch ${archiveDetail.period || ""}` : "Detail Arsip"}>
        {!archiveDetail ? <Skeleton className="h-40"/> : (
          <div className="space-y-4" data-testid="payroll-archive-detail-modal">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <ArchField label="Periode" value={archiveDetail.period} mono/>
              <ArchField label="Tipe" value={archiveDetail.period_type === "custom" ? "Mingguan/Custom" : "Bulanan"}/>
              <ArchField label="Departemen" value={archiveDetail.department || "Keseluruhan"}/>
              <ArchField label="Status Terakhir" value={archiveDetail.status || "-"}/>
              <ArchField label="Jumlah Karyawan" value={archiveDetail.employee_count || 0} mono/>
              <ArchField label="Total THP" value={formatIDR(archiveDetail.total_thp || 0)} mono/>
              <ArchField label="Terkirim" value={`${archiveDetail.sent_count || 0} / ${archiveDetail.employee_count || 0}`} mono/>
              <ArchField label="Dibuat pada" value={archiveDetail.created_at ? new Date(archiveDetail.created_at).toLocaleString("id-ID") : "-"} mono small/>
              <ArchField label="Disetujui pada" value={archiveDetail.approved_at ? new Date(archiveDetail.approved_at).toLocaleString("id-ID") : "-"} mono small/>
            </div>
            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-900 mb-1">Info Penghapusan</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <ArchField label="Dihapus oleh" value={archiveDetail.deleted_by || "-"}/>
                <ArchField label="Peran" value={archiveDetail.deleted_by_role || "-"}/>
                <ArchField label="Dihapus pada" value={archiveDetail.deleted_at ? new Date(archiveDetail.deleted_at).toLocaleString("id-ID") : "-"} mono/>
              </div>
              <div className="mt-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-900 mb-0.5">Alasan</p>
                <p className="text-sm text-stone-800">{archiveDetail.delete_reason || <span className="text-stone-400 italic">(tidak diisi)</span>}</p>
              </div>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-2">Rincian Karyawan ({(archiveDetail.items || []).length})</p>
              {archiveDetailLoading ? <Skeleton className="h-24"/> : (archiveDetail.items || []).length === 0 ? (
                <p className="text-xs text-stone-400 italic">Tidak ada item.</p>
              ) : (
                <div className="max-h-[380px] overflow-y-auto border border-stone-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 sticky top-0 text-[11px] uppercase tracking-widest text-stone-500 border-b border-stone-100">
                      <tr>
                        <th className="py-2 px-3 text-left">Karyawan</th>
                        <th className="py-2 px-3 text-left">Departemen</th>
                        <th className="py-2 px-3 text-right">Lembur (jam)</th>
                        <th className="py-2 px-3 text-right">Gaji Pokok</th>
                        <th className="py-2 px-3 text-right">Upah Lembur</th>
                        <th className="py-2 px-3 text-right">Potongan</th>
                        <th className="py-2 px-3 text-right">THP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(archiveDetail.items || []).map((it, i) => (
                        <tr key={it.employee_id || i} className="border-b border-stone-100">
                          <td className="py-2 px-3 font-medium text-stone-900">{it.name || "-"} {it.employee_code && <span className="text-[10px] text-stone-400">({it.employee_code})</span>}</td>
                          <td className="py-2 px-3 text-[11px] text-stone-700">{it.department || "-"}</td>
                          <td className="py-2 px-3 text-right font-mono">{it.overtime_hours || 0}</td>
                          <td className="py-2 px-3 text-right font-mono">{formatIDR(it.gaji_pokok || 0)}</td>
                          <td className="py-2 px-3 text-right font-mono text-emerald-700">{formatIDR(it.upah_lembur || 0)}</td>
                          <td className="py-2 px-3 text-right font-mono text-red-700">{formatIDR(it.total_potongan || 0)}</td>
                          <td className="py-2 px-3 text-right font-mono font-semibold text-blue-800">{formatIDR(it.take_home_pay || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="flex justify-end pt-3 border-t border-stone-100">
              <Button variant="secondary" onClick={() => setArchiveOpen(false)}>Tutup</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Iter v34b6 — Create Batch Modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} size="lg" title="Buat Batch Payroll Baru">
        <div className="space-y-4" data-testid="payroll-create-modal">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-2">Tipe Periode</label>
            <div className="flex gap-2">
              <button
                onClick={() => setForm((f) => ({ ...f, period_type: "monthly" }))}
                className={`flex-1 border rounded-xl p-3 text-left ${form.period_type === "monthly" ? "border-blue-500 bg-blue-50" : "border-stone-200 hover:border-stone-300"}`}
                data-testid="payroll-type-monthly"
              >
                <div className="font-semibold text-sm">Bulanan</div>
                <div className="text-[11px] text-stone-500">Pilih bulan penuh (mis: Juli 2026)</div>
              </button>
              <button
                onClick={() => setForm((f) => ({ ...f, period_type: "custom" }))}
                className={`flex-1 border rounded-xl p-3 text-left ${form.period_type === "custom" ? "border-blue-500 bg-blue-50" : "border-stone-200 hover:border-stone-300"}`}
                data-testid="payroll-type-custom"
              >
                <div className="font-semibold text-sm">Mingguan / Rentang Custom</div>
                <div className="text-[11px] text-stone-500">Pilih tanggal mulai & selesai bebas</div>
              </button>
            </div>
          </div>

          {form.period_type === "monthly" ? (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Bulan (YYYY-MM)</label>
              <Input type="month" value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))} data-testid="payroll-new-period"/>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Tanggal Mulai</label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="payroll-start-date"/>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Tanggal Selesai</label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} data-testid="payroll-end-date"/>
              </div>
            </div>
          )}

          {/* Iter v34b8 — Filter Departemen */}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Cakupan Karyawan</label>
            <select
              value={form.department}
              onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
              className="w-full border border-stone-300 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="payroll-department-select"
            >
              <option value="">Semua Karyawan (Keseluruhan)</option>
              {departments.map((d) => (
                <option key={d.id || d.name} value={d.name}>{d.name} (per-departemen)</option>
              ))}
            </select>
            <p className="text-[11px] text-stone-500 mt-1">
              Pilih departemen bila ingin generate payroll hanya untuk 1 departemen. Batch per-departemen bisa coexist dengan batch keseluruhan pada period yang sama.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">Komponen Master Aktif</label>
              <button onClick={() => navigate("/payroll/components")} className="text-[11px] text-blue-700 hover:underline">+ Tambah komponen baru</button>
            </div>
            {components.length === 0 ? (
              <div className="text-xs text-stone-400 italic p-3 border border-stone-200 rounded-xl">
                Belum ada komponen master. Buka <b>Master Komponen</b> untuk menambahkan Uang Makan, BPJS, dsb.
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto border border-stone-200 rounded-xl p-2">
                {components.map((c) => (
                  <label key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 cursor-pointer" data-testid={`payroll-comp-${c.id}`}>
                    <input
                      type="checkbox"
                      checked={activeComponentIds.includes(c.id)}
                      onChange={() => toggleComp(c.id)}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {c.name}
                        <span className={`ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${c.kind === "earning" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                          {c.kind === "earning" ? "Tunjangan" : "Potongan"}
                        </span>
                      </div>
                      <div className="text-[11px] text-stone-500 font-mono-num">
                        {formatIDR(c.amount)} · {c.calc_mode === "per_work_day" ? "per hari kerja" : c.calc_mode === "per_overtime_hour" ? "per jam lembur" : "flat"}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Batal</Button>
            <Button onClick={createBatch} disabled={creating} data-testid="payroll-create-btn">
              {creating ? "Membuat..." : "Buat Batch"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}


// Iter v34ch — Helper untuk detail arsip batch
function ArchField({ label, value, mono, small }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">{label}</p>
      <p className={`${mono ? "font-mono" : ""} ${small ? "text-[11px]" : "text-sm"} text-stone-800 break-words`}>{value}</p>
    </div>
  );
}

function StatusPill({ s }) {  const map = {
    draft: ["Draft", "bg-stone-100 text-stone-700"],
    approved: ["Approved", "bg-blue-100 text-blue-800"],
    sent: ["Terkirim", "bg-emerald-100 text-emerald-800"],
  };
  const [label, cls] = map[s] || [s, "bg-stone-100 text-stone-700"];
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-semibold ${cls}`}>{label}</span>;
}

/* ============================ DETAIL PAGE ============================ */
export function PayrollDetail() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/payroll/batches/${batchId}`);
      setBatch(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [batchId]);

  const approve = async () => {
    if (!window.confirm("Approve batch ini? Setelah approve baru bisa dikirim ke email.")) return;
    try {
      await api.post(`/payroll/batches/${batchId}/approve`);
      toast.success("Batch di-approve");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal approve"); }
  };

  const downloadPdf = async (empId, name) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/payroll/batches/${batchId}/slip/${empId}.pdf`;
      window.open(url, "_blank");
    } catch (e) { toast.error("Gagal download PDF"); }
  };

  const sendEmails = async (targetIds) => {
    if (batch?.status === "draft") { toast.error("Approve batch dulu sebelum kirim email"); return; }
    const count = targetIds ? targetIds.length : batch?.items?.length || 0;
    if (!window.confirm(`Kirim email slip gaji ke ${count} karyawan?`)) return;
    setSending(true);
    try {
      const r = await api.post(`/payroll/batches/${batchId}/send-email`, { employee_ids: targetIds || null });
      toast.success(`Terkirim: ${r.data.sent}/${count} (${r.data.failed} gagal)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal kirim"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="p-8"><Skeleton className="h-64 w-full"/></div>;
  if (!batch) return <div className="p-8 text-center text-stone-400">Batch tidak ditemukan</div>;

  const totalPend = batch.items.reduce((s, i) => s + (Number(i.total_pendapatan) || 0), 0);
  const totalPot = batch.items.reduce((s, i) => s + (Number(i.total_potongan) || 0), 0);
  const totalThp = batch.items.reduce((s, i) => s + (Number(i.take_home_pay) || 0), 0);
  const sentCount = batch.items.filter((i) => i.status === "sent").length;

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1500px] mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/payroll")} className="flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900" data-testid="payroll-back-list">
            <ArrowLeft className="h-4 w-4"/> Kembali ke Daftar Batch
          </button>
          <StatusPill s={batch.status}/>
        </div>

        <PageHeader
          overline={`Payroll · Periode ${batch.period}`}
          title="Detail Batch Payroll"
          description={`${batch.items.length} karyawan · Klik row untuk edit gaji pokok, bonus, potongan, atau upload bukti transfer.`}
          action={
            <div className="flex items-center gap-2">
              {batch.status === "draft" && (
                <Button onClick={approve} data-testid="payroll-approve-btn"><CheckCircle2 className="h-3.5 w-3.5"/> Approve Batch</Button>
              )}
              {batch.status !== "draft" && (
                <Button onClick={() => sendEmails(null)} disabled={sending} data-testid="payroll-send-all-btn">
                  <Send className="h-3.5 w-3.5"/> {sending ? "Mengirim..." : "Kirim ke Semua (Bulk)"}
                </Button>
              )}
            </div>
          }
        />

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard icon={Users} label="Total Karyawan" value={batch.items.length}/>
          <SummaryCard icon={Wallet} label="Total Pendapatan" value={formatIDR(totalPend)}/>
          <SummaryCard icon={FileText} label="Total Potongan" value={formatIDR(totalPot)} tone="red"/>
          <SummaryCard icon={Mail} label="Terkirim" value={`${sentCount}/${batch.items.length}`} tone="blue"/>
        </div>

        {/* Items table */}
        <Card className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payroll-items-table">
              <thead className="bg-stone-50">
                <tr className="text-left text-[10px] uppercase tracking-widest text-stone-500 border-b">
                  <th className="py-2 px-3">Karyawan</th>
                  <th className="py-2 px-3 text-right">Gaji Pokok</th>
                  <th className="py-2 px-3 text-right">Bonus</th>
                  <th className="py-2 px-3 text-right">Upah Lembur</th>
                  <th className="py-2 px-3 text-right">Bon</th>
                  <th className="py-2 px-3 text-right">Pot. Lain</th>
                  <th className="py-2 px-3 text-right">THP</th>
                  <th className="py-2 px-3 text-center">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {batch.items.map((it) => (
                  <tr key={it.employee_id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`payroll-item-${it.employee_id}`}>
                    <td className="py-2 px-3">
                      <div className="font-medium text-stone-900">{it.name}</div>
                      <div className="text-[10px] text-stone-500">{it.department} · {it.position}</div>
                    </td>
                    <td className="py-2 px-3 text-right font-mono-num text-xs">{formatIDR(it.gaji_pokok)}</td>
                    <td className="py-2 px-3 text-right font-mono-num text-xs">{formatIDR(it.bonus)}</td>
                    <td className="py-2 px-3 text-right font-mono-num text-xs">
                      {formatIDR(it.upah_lembur)}
                      {it.overtime_hours > 0 && <div className="text-[9px] text-stone-400">{it.overtime_hours}h</div>}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-num text-xs text-red-700">{formatIDR(it.potongan_bon)}</td>
                    <td className="py-2 px-3 text-right font-mono-num text-xs text-red-700">{formatIDR(it.potongan_manual)}</td>
                    <td className="py-2 px-3 text-right font-mono-num font-semibold text-blue-800">{formatIDR(it.take_home_pay)}</td>
                    <td className="py-2 px-3 text-center"><StatusPill s={it.status}/></td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      <button onClick={() => setEditItem(it)} className="text-stone-500 hover:text-blue-700 px-2" data-testid={`payroll-edit-${it.employee_id}`} disabled={batch.status === "sent"}><FileText className="h-3.5 w-3.5"/></button>
                      <button onClick={() => downloadPdf(it.employee_id, it.name)} className="text-stone-500 hover:text-blue-700 px-2" data-testid={`payroll-pdf-${it.employee_id}`}><Download className="h-3.5 w-3.5"/></button>
                      {batch.status !== "draft" && (
                        <button onClick={() => sendEmails([it.employee_id])} className="text-emerald-600 hover:text-emerald-800 px-2" data-testid={`payroll-send-${it.employee_id}`}><Send className="h-3.5 w-3.5"/></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-blue-50/60 font-semibold text-blue-900">
                <tr>
                  <td colSpan={6} className="py-3 px-3 uppercase tracking-widest text-xs">Total</td>
                  <td className="py-3 px-3 text-right font-mono-num" data-testid="payroll-total-thp">{formatIDR(totalThp)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </div>

      {/* Item edit modal */}
      <Modal open={!!editItem} onClose={() => setEditItem(null)} size="lg" title={editItem ? `Edit — ${editItem.name}` : ""}>
        {editItem && <ItemEditForm batchId={batchId} item={editItem} onSaved={(next) => { setEditItem(null); load(); }}/>}
      </Modal>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, tone }) {
  const cls = tone === "blue" ? "bg-gradient-to-br from-blue-50 to-white border-blue-100"
    : tone === "red" ? "bg-gradient-to-br from-red-50 to-white border-red-100"
    : "bg-white border-stone-200/70";
  return (
    <div className={`rounded-2xl border p-4 ${cls}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
        <Icon className="h-3.5 w-3.5"/> {label}
      </div>
      <div className={`font-display text-xl mt-2 font-mono-num ${tone === "blue" ? "text-blue-800" : tone === "red" ? "text-red-800" : "text-stone-900"}`}>{value}</div>
    </div>
  );
}

function ItemEditForm({ batchId, item, onSaved }) {
  const [form, setForm] = useState({
    ...item,
    extra_earnings: item.extra_earnings || [],
    extra_deductions: item.extra_deductions || [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const num = (v) => Math.max(0, Number(v) || 0);

  // Iter v34b6 — Extras helpers
  const addExtra = (kind) => {
    const key = kind === "earning" ? "extra_earnings" : "extra_deductions";
    const newLine = { id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: "", amount: 0, show: true };
    setForm((p) => ({ ...p, [key]: [...(p[key] || []), newLine] }));
  };
  const editExtra = (kind, idx, patch) => {
    const key = kind === "earning" ? "extra_earnings" : "extra_deductions";
    setForm((p) => {
      const list = [...(p[key] || [])];
      list[idx] = { ...list[idx], ...patch };
      return { ...p, [key]: list };
    });
  };
  const rmExtra = (kind, idx) => {
    const key = kind === "earning" ? "extra_earnings" : "extra_deductions";
    setForm((p) => ({ ...p, [key]: (p[key] || []).filter((_, i) => i !== idx) }));
  };

  const extraEarnSum = (form.extra_earnings || []).filter((x) => x.show !== false).reduce((s, x) => s + Number(x.amount || 0), 0);
  const extraDedSum = (form.extra_deductions || []).filter((x) => x.show !== false).reduce((s, x) => s + Number(x.amount || 0), 0);
  const totalPend = num(form.gaji_pokok) + num(form.bonus) + num(form.upah_lembur) + extraEarnSum;
  const totalPot = num(form.potongan_bon) + num(form.potongan_manual) + extraDedSum;
  const thp = totalPend - totalPot;

  const uploadBukti = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("bukti_transfer_id", up.data.id);
      setField("bukti_transfer_name", up.data.filename);
      toast.success("Bukti transfer terupload");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleanExtras = (list) => (list || []).map((x) => ({
        id: x.id && !String(x.id).startsWith("new-") ? x.id : undefined,
        component_id: x.component_id || null,
        name: (x.name || "").trim() || "-",
        amount: Number(x.amount) || 0,
        show: x.show !== false,
      }));
      const payload = {
        gaji_pokok: num(form.gaji_pokok),
        bonus: num(form.bonus),
        upah_lembur: num(form.upah_lembur),
        overtime_hours: num(form.overtime_hours),
        potongan_bon: num(form.potongan_bon),
        potongan_manual: num(form.potongan_manual),
        show_gaji_pokok: !!form.show_gaji_pokok,
        show_bonus: !!form.show_bonus,
        show_lembur: !!form.show_lembur,
        show_bon: !!form.show_bon,
        show_potongan_manual: !!form.show_potongan_manual,
        note: form.note || "",
        bukti_transfer_id: form.bukti_transfer_id || null,
        bukti_transfer_name: form.bukti_transfer_name || null,
        extra_earnings: cleanExtras(form.extra_earnings),
        extra_deductions: cleanExtras(form.extra_deductions),
      };
      await api.patch(`/payroll/batches/${batchId}/items/${item.employee_id}`, payload);
      toast.success("Perubahan disimpan");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="text-[11px] text-stone-500">
        {item.department} · {item.position} · Bank: {item.bank_name || "-"} {item.bank_account_number}
        {typeof item.work_days === "number" && <span className="ml-2">· <b>{item.work_days}</b> hari hadir</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <NumField label="Gaji Pokok" value={form.gaji_pokok} onChange={(v) => setField("gaji_pokok", v)} showVisible visible={form.show_gaji_pokok} onToggleVisible={() => setField("show_gaji_pokok", !form.show_gaji_pokok)} testid="edit-gaji-pokok"/>
        <NumField label="Bonus" value={form.bonus} onChange={(v) => setField("bonus", v)} showVisible visible={form.show_bonus} onToggleVisible={() => setField("show_bonus", !form.show_bonus)} testid="edit-bonus"/>
        <NumField label={`Upah Lembur (${form.overtime_hours || 0} jam)`} value={form.upah_lembur} onChange={(v) => setField("upah_lembur", v)} showVisible visible={form.show_lembur} onToggleVisible={() => setField("show_lembur", !form.show_lembur)} testid="edit-lembur"/>
        <NumField label="Potongan Bon/Kasbon" value={form.potongan_bon} onChange={(v) => setField("potongan_bon", v)} showVisible visible={form.show_bon} onToggleVisible={() => setField("show_bon", !form.show_bon)} testid="edit-bon"/>
        <NumField label="Potongan Manual (Lainnya)" value={form.potongan_manual} onChange={(v) => setField("potongan_manual", v)} showVisible visible={form.show_potongan_manual} onToggleVisible={() => setField("show_potongan_manual", !form.show_potongan_manual)} testid="edit-potongan-manual"/>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Catatan (opsional)</label>
          <Input value={form.note || ""} onChange={(e) => setField("note", e.target.value)} placeholder="Catatan untuk slip gaji" data-testid="edit-note"/>
        </div>
      </div>

      {/* Iter v34b6 — Extra earnings editor */}
      <ExtrasEditor
        title="Tunjangan/Pendapatan Tambahan"
        color="emerald"
        list={form.extra_earnings || []}
        onAdd={() => addExtra("earning")}
        onChange={(i, p) => editExtra("earning", i, p)}
        onRemove={(i) => rmExtra("earning", i)}
        testidPrefix="edit-extra-earn"
      />

      {/* Iter v34b6 — Extra deductions editor */}
      <ExtrasEditor
        title="Potongan Tambahan"
        color="red"
        list={form.extra_deductions || []}
        onAdd={() => addExtra("deduction")}
        onChange={(i, p) => editExtra("deduction", i, p)}
        onRemove={(i) => rmExtra("deduction", i)}
        testidPrefix="edit-extra-ded"
      />

      <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4 grid grid-cols-3 gap-3">
        <div><div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">Total Pendapatan</div><div className="font-display text-lg font-mono-num text-blue-800">{formatIDR(totalPend)}</div></div>
        <div><div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Total Potongan</div><div className="font-display text-lg font-mono-num text-red-800">{formatIDR(totalPot)}</div></div>
        <div><div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Take Home Pay</div><div className="font-display text-lg font-mono-num text-emerald-800">{formatIDR(thp)}</div></div>
      </div>

      {/* Bukti transfer upload */}
      <div className="rounded-2xl border border-stone-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">Bukti Transfer</label>
          <label className="cursor-pointer flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-900">
            <Upload className="h-3.5 w-3.5"/> {form.bukti_transfer_id ? "Ganti" : "Upload"}
            <input type="file" accept="image/*,.pdf" className="hidden" onChange={uploadBukti} data-testid="edit-upload-bukti"/>
          </label>
        </div>
        {form.bukti_transfer_id ? (
          <div className="text-xs text-stone-700 flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600"/> {form.bukti_transfer_name || "Terupload"}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${form.bukti_transfer_id}`} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">Lihat</a>
          </div>
        ) : (
          <div className="text-xs text-stone-400 italic">Belum ada bukti transfer</div>
        )}
        {uploading && <div className="text-xs text-blue-600 mt-1">Mengupload...</div>}
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
        <Button onClick={save} disabled={saving} data-testid="edit-save-btn"><Save className="h-3.5 w-3.5"/>{saving ? "Menyimpan..." : "Simpan"}</Button>
      </div>
    </div>
  );
}

function ExtrasEditor({ title, color, list, onAdd, onChange, onRemove, testidPrefix }) {
  const tone = color === "emerald"
    ? "text-emerald-800 bg-emerald-50 border-emerald-100"
    : "text-red-800 bg-red-50 border-red-100";
  return (
    <div className={`rounded-2xl border p-4 ${tone}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider">{title}</div>
        <Button onClick={onAdd} variant="secondary" className="!py-1 !px-2" data-testid={`${testidPrefix}-add`}>
          <Plus className="h-3 w-3"/> Tambah
        </Button>
      </div>
      {list.length === 0 ? (
        <div className="text-xs text-stone-500 italic py-2">Belum ada item.</div>
      ) : (
        <div className="space-y-2">
          {list.map((x, i) => (
            <div key={x.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`${testidPrefix}-row-${i}`}>
              <Input
                value={x.name || ""}
                onChange={(e) => onChange(i, { name: e.target.value })}
                placeholder="Nama (mis: Uang Makan)"
                className="col-span-6"
                data-testid={`${testidPrefix}-name-${i}`}
              />
              <Input
                type="number"
                min="0"
                step="1000"
                value={x.amount || 0}
                onChange={(e) => onChange(i, { amount: Number(e.target.value) || 0 })}
                className="col-span-4 font-mono-num text-right"
                data-testid={`${testidPrefix}-amount-${i}`}
              />
              <button
                onClick={() => onChange(i, { show: !(x.show !== false) })}
                className={`col-span-1 flex items-center justify-center px-1 py-1.5 rounded ${x.show !== false ? "text-emerald-700" : "text-stone-400"}`}
                title="Tampilkan di slip?"
                data-testid={`${testidPrefix}-visible-${i}`}
              >
                {x.show !== false ? <Eye className="h-4 w-4"/> : <EyeOff className="h-4 w-4"/>}
              </button>
              <button onClick={() => onRemove(i)} className="col-span-1 text-stone-400 hover:text-red-700 px-1" data-testid={`${testidPrefix}-remove-${i}`}>
                <X className="h-4 w-4"/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NumField({ label, value, onChange, showVisible, visible, onToggleVisible, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600">{label}</label>
        {showVisible && (
          <button
            onClick={onToggleVisible}
            className={`flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${visible ? "text-emerald-700 bg-emerald-50" : "text-stone-500 bg-stone-100"}`}
            data-testid={`${testid}-visible`}
            title="Toggle tampilkan di slip PDF & email"
          >
            {visible ? <><Eye className="h-2.5 w-2.5"/> Tampil</> : <><EyeOff className="h-2.5 w-2.5"/> Sembunyi</>}
          </button>
        )}
      </div>
      <Input type="number" min="0" step="1000" value={value || 0} onChange={(e) => onChange(e.target.value)} className="font-mono-num" data-testid={testid}/>
    </div>
  );
}
